import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { KeyRound, Plus, Trash2, RefreshCw } from 'lucide-react';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import {
  Button,
  Input,
  Label,
  Textarea,
  IconButton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useSshKeys,
  useCreateSshKey,
  useDeleteSshKey,
  type SshKey,
  type SshKeyCreated,
} from '@/api/opennebula';
import { LargeOptionPicker } from './components/LargeOptionPicker';
import { ListPageShell, TableSkeleton, ErrorState } from './components/ListPageShell';

export function SshKeys() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { data: keys = [], isLoading, isError, refetch } = useSshKeys(serviceId);
  const [createOpen, setCreateOpen] = useState(false);
  const [del, setDel] = useState<SshKey | null>(null);

  const remove = useDeleteSshKey(serviceId!, {
    onSuccess: () => {
      show({ tone: 'success', title: 'SSH key deleted' });
      setDel(null);
    },
    onError: () => show({ tone: 'danger', title: 'Delete failed' }),
  });

  const columns: ColumnDef<SshKey, unknown>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>{row.original.name || row.original.id}</span>
      ),
    },
    {
      id: 'fingerprint',
      header: 'Fingerprint',
      cell: ({ row }) => (
        <code style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          {row.original.fingerprint || '—'}
        </code>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ textAlign: 'right' }}>
          <IconButton variant="ghost" size="sm" aria-label="Delete key" onClick={() => setDel(row.original)}>
            <Trash2 size={15} />
          </IconButton>
        </div>
      ),
    },
  ];

  const addBtn = (
    <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setCreateOpen(true)}>
      Add key
    </Button>
  );

  return (
    <ListPageShell
      icon={<KeyRound size={22} />}
      title="SSH keys"
      description="Public keys available when launching instances."
      actions={
        <>
          <Button size="sm" variant="secondary" startIcon={<RefreshCw size={15} />} onClick={() => refetch()}>
            Refresh
          </Button>
          {addBtn}
        </>
      }
    >
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <ErrorState description="Could not load SSH keys for this service." onRetry={() => refetch()} />
      ) : (
        // DataTable owns the bordered frame; the empty placeholder renders
        // inside it via `emptyState`, so empty and populated share one frame
        // (no extra Card wrapper, no double border).
        <DataTable
          columns={columns}
          data={keys}
          getRowId={(k) => String(k.id)}
          emptyState={
            <EmptyState
              icon={<KeyRound size={28} />}
              title="No SSH keys"
              description="Add a public key to use key-based login on new instances."
              actions={addBtn}
            />
          }
        />
      )}

      <AddKeyDialog open={createOpen} onOpenChange={setCreateOpen} serviceId={serviceId!} />

      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete SSH key?"
        description={`Remove “${del?.name || del?.id}”? Instances already using it are unaffected.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => del && remove.mutate(del.id)}
      />
    </ListPageShell>
  );
}

/** Trigger a browser download of the generated private key (only returned once). */
function downloadPrivateKey(filename: string, content: string) {
  const url = window.URL.createObjectURL(new Blob([content]));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

type KeyMode = 'generate' | 'import';

function AddKeyDialog({
  open,
  onOpenChange,
  serviceId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
}) {
  const { show } = useToast();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<KeyMode>('generate');
  const [content, setContent] = useState('');

  const reset = () => {
    setName('');
    setMode('generate');
    setContent('');
  };

  const create = useCreateSshKey(serviceId, {
    onSuccess: (created: SshKeyCreated) => {
      // In `generate` mode the server returns the PRIVATE key once — download it.
      if (mode === 'generate' && created.content) {
        downloadPrivateKey(`${name.trim() || created.name || 'ssh-key'}.pem`, created.content);
        show({ tone: 'success', title: 'SSH key generated', description: 'Private key downloaded — keep it safe.' });
      } else {
        show({ tone: 'success', title: 'SSH key added' });
      }
      onOpenChange(false);
      reset();
    },
    onError: () => show({ tone: 'danger', title: 'Could not add key' }),
  });

  const valid = name.trim().length > 0 && (mode === 'generate' || content.trim().length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add SSH key</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <LargeOptionPicker
              columns={2}
              value={mode}
              onChange={(v) => setMode(v as KeyMode)}
              options={[
                {
                  value: 'generate',
                  title: 'Auto-generate keypair',
                  subtitle:
                    'We store the key in our secure vault. The private key downloads to your device on creation and is never retrievable again.',
                },
                {
                  value: 'import',
                  title: 'Import existing keypair',
                  subtitle: 'Paste your public key so you can reuse it across instances.',
                },
              ]}
            />
            <div>
              <Label htmlFor="ssh-key-name">Name</Label>
              <Input id="ssh-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="my-laptop" />
            </div>
            {mode === 'import' && (
              <div>
                <Label htmlFor="ssh-key-content">Public key</Label>
                <Textarea
                  id="ssh-key-content"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="ssh-ed25519 AAAA…"
                  rows={4}
                  style={{ fontFamily: 'var(--vn-font-mono, monospace)' }}
                />
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || create.isPending}
            onClick={() =>
              create.mutate({
                name: name.trim(),
                type: mode,
                ...(mode === 'import' ? { content: content.trim() } : {}),
              })
            }
          >
            {mode === 'generate' ? 'Generate key' : 'Add key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
